/**
 * service-mail mail-item read client. Mirrors:
 *   apps/service-mail/src/modules/mail-item/mail-item.controller.ts
 *
 * Phase 2a only needs the LIST endpoint for the dashboard "recent mail items"
 * widget. The full mail-item surface (transitions, sort-bin, item detail)
 * lands with Phase 2b.
 *
 * RBAC note: every method requires `Mail/readMailItem` and is operator-scoped.
 * A dev-paste JWT without that permission gets a 403 — the dashboard widget
 * surfaces that as a "Permission required — service-identity rollout deferred" notice
 * rather than failing the whole page.
 */
import { env } from '../env';

import { request } from './client';

export interface MailItemSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly trackingNumber: string;
  readonly status: string;
  readonly serviceClass: string;
  readonly registeredBy: string;
  readonly registeredAt: string;
  readonly updatedAt: string;
}

/**
 * Operator-only detail projection. Includes the denormalised JSON sender,
 * receiver, package, and pricing blocks. Returned by GET /v1/mail-items/:id.
 *
 * The shapes of the JSON blocks are intentionally loose (`Record<string,
 * unknown>`) — they mirror the JSON columns and the renderers in
 * MailItemDetailPage tolerate missing keys.
 */
export interface MailItemDetail extends MailItemSummary {
  readonly sender: Readonly<Record<string, unknown>>;
  readonly receiver: Readonly<Record<string, unknown>>;
  readonly package: Readonly<Record<string, unknown>>;
  readonly pricing: Readonly<Record<string, unknown>>;
}

export interface ListMailItemsResponse {
  readonly items: readonly MailItemSummary[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface ListMailItemsQuery {
  readonly status?: string;
  readonly trackingNumber?: string;
  /** Filter by sender/receiver customer id — used by the customer-360 page. */
  readonly customerId?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function listMailItems(
  query: ListMailItemsQuery = {},
): Promise<ListMailItemsResponse> {
  return request<ListMailItemsResponse>(baseUrl(), '/v1/mail-items', {
    method: 'GET',
    query: {
      status: query.status,
      trackingNumber: query.trackingNumber,
      customerId: query.customerId,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getMailItem(id: string): Promise<MailItemDetail> {
  return request<MailItemDetail>(baseUrl(), `/v1/mail-items/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * Operator-only tracking-number → mail-item-id resolver. Returns the minimal
 * `{id, trackingNumber}` projection so the caller can chain `getMailItem(id)`
 * for the full receipt details.
 *
 * Backed by `GET /v1/mail-items/lookup?trackingNumber=...` (Mail/readMailItem).
 */
export interface MailItemLookupResponse {
  readonly id: string;
  readonly trackingNumber: string;
}

export async function lookupMailItemByTracking(
  trackingNumber: string,
): Promise<MailItemLookupResponse> {
  return request<MailItemLookupResponse>(baseUrl(), '/v1/mail-items/lookup', {
    method: 'GET',
    query: { trackingNumber },
  });
}

// ---------------------------------------------------------------------------
// Aggregate / dashboard endpoints
// ---------------------------------------------------------------------------

export interface SortBoardBin {
  readonly binCode: string;
  readonly laneClass: string;
  readonly nextHubCode: string;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly byStatus: Readonly<Record<string, number>>;
}

export interface SortBoardResponse {
  readonly bins: readonly SortBoardBin[];
   
  readonly totalUnsorted: number;
   
  readonly totalItems: number;
}

export async function getSortingBoard(): Promise<SortBoardResponse> {
  return request<SortBoardResponse>(baseUrl(), '/v1/sorting/board', { method: 'GET' });
}

export interface MailDashboardDayBucket {
  readonly date: string;
   
  readonly count: number;
}

export interface MailDashboardStatsResponse {
  readonly today: {
    // eslint-disable-next-line upmp/no-number-money -- row count, not money
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
  };
  readonly period: {
    readonly fromDate: string;
    readonly toDate: string;
    // eslint-disable-next-line upmp/no-number-money -- row count, not money
    readonly total: number;
    readonly totalsByDay: readonly MailDashboardDayBucket[];
    readonly totalsByStatus: Readonly<Record<string, number>>;
  };
}

export async function getMailDashboardStats(
  days = 30,
): Promise<MailDashboardStatsResponse> {
  return request<MailDashboardStatsResponse>(
    baseUrl(),
    '/v1/mail-items/stats/dashboard',
    { method: 'GET', query: { days } },
  );
}

export interface TransitionRequest {
  readonly event:
    | 'PAY'
    | 'REGISTER'
    | 'SORT'
    | 'DISPATCH'
    | 'OUT_FOR_DELIVERY'
    | 'DELIVER'
    | 'RETURN'
    | 'LOSE'
    | 'DAMAGE';
  readonly locationCode?: string;
  readonly notes?: string;
  readonly idempotencyKey?: string;
}

export interface TransitionResponse {
  readonly mailItem: MailItemSummary;
  readonly event: {
    readonly id: string;
    readonly mailItemId: string;
    readonly eventType: string;
    readonly locationCode: string | null;
    readonly notes: string | null;
    readonly recordedBy: string;
    readonly recordedAt: string;
  };
}

export async function transitionMailItem(
  id: string,
  body: TransitionRequest,
): Promise<TransitionResponse> {
  return request<TransitionResponse>(
    baseUrl(),
    `/v1/mail-items/${encodeURIComponent(id)}/transitions`,
    { method: 'POST', body },
  );
}

export interface PublicTrackingResponse {
  readonly trackingNumber: string;
  readonly status: string;
  readonly serviceClass: string;
  readonly registeredAt: string;
  readonly events: readonly {
    readonly eventType: string;
    readonly locationCode: string | null;
    readonly recordedAt: string;
  }[];
}

export async function trackByNumber(trackingNumber: string): Promise<PublicTrackingResponse> {
  return request<PublicTrackingResponse>(
    baseUrl(),
    `/v1/track/${encodeURIComponent(trackingNumber)}`,
    { method: 'GET' },
  );
}

// ---------------------------------------------------------------------------
// F-076 address-change-request — operator (approval queue) endpoints
// ---------------------------------------------------------------------------

export interface AddressChangeRequestDto {
  readonly id: string;
  readonly mailItemId: string;
  readonly requestedBy: string;
  readonly reason: string;
  readonly newLine1: string;
  readonly newCity: string;
  readonly newState: string;
  readonly newPostalCode: string | null;
  readonly status: string;
  readonly redirectFeeNgn: string | null;
  readonly crossZone: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly approvedByOperatorId: string | null;
  readonly approvedAt: string | null;
  readonly rejectedByOperatorId: string | null;
  readonly rejectedAt: string | null;
  readonly rejectionReason: string | null;
  readonly appliedByOperatorId: string | null;
  readonly appliedAt: string | null;
}

/**
 * Extended DTO returned by GET /v1/mail-items/address-change-requests/pending.
 * Includes `trackingNumber` so the staff console can call the approve/reject
 * endpoints which require it in the URL path.
 */
export interface PendingAddressChangeRequestDto extends AddressChangeRequestDto {
  readonly trackingNumber: string;
}

/**
 * GET /v1/mail-items/address-change-requests/pending
 *
 * Returns all address-change requests in REQUESTED status (cross-zone only,
 * since same-zone requests auto-approve at submission). Used by the staff
 * console ApprovalQueuePage. Requires Mail/listPendingApprovals permission.
 */
export async function listPendingAddressChangeRequests(): Promise<readonly PendingAddressChangeRequestDto[]> {
  return request<readonly PendingAddressChangeRequestDto[]>(
    baseUrl(),
    '/v1/mail-items/address-change-requests/pending',
    { method: 'GET' },
  );
}

/**
 * POST /v1/mail-items/:trackingNumber/address-change-requests/:requestId/approve
 *
 * Transitions status from REQUESTED to APPROVED.
 * Requires Mail/approveAddressChange permission.
 */
export async function approveAddressChangeRequest(
  trackingNumber: string,
  requestId: string,
): Promise<AddressChangeRequestDto> {
  return request<AddressChangeRequestDto>(
    baseUrl(),
    `/v1/mail-items/${encodeURIComponent(trackingNumber)}/address-change-requests/${encodeURIComponent(requestId)}/approve`,
    { method: 'POST' },
  );
}

/**
 * POST /v1/mail-items/:trackingNumber/address-change-requests/:requestId/reject
 *
 * Transitions status from REQUESTED to REJECTED.
 * Requires Mail/rejectAddressChange permission.
 */
export async function rejectAddressChangeRequest(
  trackingNumber: string,
  requestId: string,
  rejectionReason: string,
): Promise<AddressChangeRequestDto> {
  return request<AddressChangeRequestDto>(
    baseUrl(),
    `/v1/mail-items/${encodeURIComponent(trackingNumber)}/address-change-requests/${encodeURIComponent(requestId)}/reject`,
    { method: 'POST', body: { rejectionReason } },
  );
}
